import React, { useState, useEffect, useMemo } from "react";
import { Select, Spin, message, Modal } from "antd";
import { FileTextOutlined, MoreOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import api from "../services/api";

const { Option } = Select;

// ── LOGOS MAP ────────────────────────────────────────────────────────────────
const LOGOS_MAP = [
  { keys: ["livelo"], bg: "#e8008b", letra: "LV" },
  { keys: ["itaú pontos", "itau pontos"], bg: "#d48c0c", letra: "IT" },
  { keys: ["smiles"], bg: "#ff6600", letra: "S" },
  { keys: ["azul fidelidade", "azul"], bg: "#003087", letra: "A" },
  { keys: ["coopera pj"], bg: "#007b5e", letra: "CPJ" },
  { keys: ["coopera"], bg: "#007b5e", letra: "COO" },
  { keys: ["latam"], bg: "#d81a36", letra: "L" },
  { keys: ["tap miles", "tap"], bg: "#009900", letra: "TAP" },
  { keys: ["max milhas"], bg: "#1e3a6e", letra: "MM" },
  { keys: ["hotmilhas"], bg: "#cc3300", letra: "HM" },
  { keys: ["ipiranga"], bg: "#f5a800", letra: "IP" },
  { keys: ["esfera empresas"], bg: "#8b0000", letra: "EE" },
  { keys: ["esfera"], bg: "#8b0000", letra: "E_" },
  { keys: ["bank milhas"], bg: "#0066cc", letra: "BM" },
  { keys: ["cash milhas"], bg: "#5a5a5a", letra: "CM" },
  { keys: ["curtaí", "curtai"], bg: "#e8a000", letra: "CT" },
  { keys: ["compro suas milhas"], bg: "#1e3a4a", letra: "CSM" },
  { keys: ["átomos", "atomos"], bg: "#000000", letra: "ÁT" },
  { keys: ["gpa"], bg: "#00aa44", letra: "GPA" },
  { keys: ["pontos caixa"], bg: "#007b5e", letra: "PC" },
  { keys: ["caixa empresas"], bg: "#007b5e", letra: "CE" },
  { keys: ["sicredi empresarial"], bg: "#009900", letra: "SCE" },
  { keys: ["sicredi"], bg: "#009900", letra: "SC" },
  { keys: ["dotz"], bg: "#ff6600", letra: "DZ" },
  { keys: ["porto plus"], bg: "#0057a8", letra: "PP" },
  { keys: ["inter loop"], bg: "#ff6b00", letra: "IL" },
  { keys: ["inter empresas"], bg: "#ff6b00", letra: "IE" },
  { keys: ["volare"], bg: "#c8102e", letra: "VL" },
  { keys: ["membership rewards", "amex"], bg: "#006fcf", letra: "MR" },
  { keys: ["safra rewards"], bg: "#003366", letra: "SR" },
  { keys: ["suma"], bg: "#1e3a4a", letra: "SU" },
  { keys: ["cressol"], bg: "#007b5e", letra: "CR" },
  { keys: ["banrisul"], bg: "#003087", letra: "BR" },
  { keys: ["unicred"], bg: "#009900", letra: "UC" },
  { keys: ["milhas plus"], bg: "#e8a000", letra: "M+" },
  { keys: ["xp pontos"], bg: "#1a1a2e", letra: "XP" },
  { keys: ["nubank"], bg: "#820ad1", letra: "NU" },
  { keys: ["ailos"], bg: "#007b5e", letra: "AI" },
  { keys: ["banestes"], bg: "#003087", letra: "BN" },
  { keys: ["bb empresas"], bg: "#f5a800", letra: "BB" },
  { keys: ["itaú empresas", "itau empresas"], bg: "#d48c0c", letra: "ITE" },
  { keys: ["pontos btg"], bg: "#1a1a2e", letra: "BTG" },
  { keys: ["iberia"], bg: "#cc0000", letra: "IB" },
  { keys: ["mileageplus", "united"], bg: "#002244", letra: "UA" },
  { keys: ["all accor", "accor"], bg: "#c8102e", letra: "AL" },
  { keys: ["Qantas", "qantas"], bg: "#b31f1f", letra: "QT" },
  { keys: ["ihg"], bg: "#6a0dad", letra: "IHG" },
  { keys: ["aadvantage", "american"], bg: "#0078d2", letra: "AA" },
  { keys: ["flying blue"], bg: "#003087", letra: "FB" },
  { keys: ["british airways"], bg: "#2b4090", letra: "BA" },
  { keys: ["qatar"], bg: "#5c0632", letra: "QR" },
  { keys: ["aeroplan"], bg: "#c8102e", letra: "AP" },
  { keys: ["copa", "connectmiles"], bg: "#0033a0", letra: "CM" },
  { keys: ["delta"], bg: "#c8102e", letra: "DL" },
  { keys: ["emirates"], bg: "#c8102e", letra: "EK" },
  { keys: ["virgin atlantic", "flying club"], bg: "#e8003d", letra: "VS" },
  { keys: ["finnair"], bg: "#003580", letra: "AY" },
  { keys: ["wyndham"], bg: "#003087", letra: "WY" },
];

const getLogo = (nome = "") => {
  if (!nome) return { bg: "#607d8b", letra: "??" };
  const n = nome.toLowerCase();
  for (const l of LOGOS_MAP) {
    if (l.keys.some((k) => n.includes(k))) return l;
  }
  return { bg: "#607d8b", letra: (nome || "??").slice(0, 2).toUpperCase() };
};

// ── LogoCircle ──────────────────────────────────────────────────────────────
const LogoCircle = ({ nome, size = 20 }) => {
  const { bg, letra } = getLogo(nome);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color: "#fff",
        fontSize: size * 0.33,
        fontWeight: 700,
        flexShrink: 0,
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
    >
      {letra}
    </span>
  );
};

// ── Helper FireDAC ──────────────────────────────────────────────────────────
const extractRows = (data) => {
  if (!data) return [];
  if (data.FDBS) {
    try {
      const tables = data.FDBS.Manager.TableList;
      if (tables?.length > 0 && tables[0].RowList)
        return tables[0].RowList.map((r) => r.Original);
    } catch (e) {}
  }
  if (data.Table && Array.isArray(data.Table)) return data.Table;
  if (Array.isArray(data)) return data;
  return [];
};

// ── Formatação ─────────────────────────────────────────────────────────────
const fmtInt = (v) => Number(v || 0).toLocaleString("pt-BR");
const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ── Formatação de Timestamp ────────────────────────────────────────────────
const fmtTimestamp = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  const match = s.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}:${match[6]}`;
  }
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const comp = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (comp) return `${comp[3]}/${comp[2]}/${comp[1]}`;
  if (s.match(/^\d{2}\/\d{2}\/\d{4}/)) return s.slice(0, 10);
  return s;
};

// data -> objeto year/month para filtro de período
const parseYearMonth = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})/);
  if (iso) return { ano: +iso[1], mes: +iso[2] };
  const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) return { ano: +br[3], mes: +br[2] };
  const ts = s.match(/^(\d{4})(\d{2})(\d{2})T/);
  if (ts) return { ano: +ts[1], mes: +ts[2] };
  return null;
};

// ── Mapa de rótulos de operação ──────────────────────────────────────────
const LABEL_CATEGORIA = {
  CADASTRO_PROGRAMA: "Abertura de Conta",
  AJUSTE_MANUAL: "Manual",
  COMPRA: "Compra",
  COMPRA_TURBINADA: "Compra Turbinada",
  BUMERANGUE: "Bumerangue",
  TRANSFERENCIA_DEBITO: "Transferência",
  TRANSFERENCIA_CREDITO: "Transferência",
  TRANSFERENCIA_PARIDADE_DEBITO: "Transfer com Paridade",
  TRANSFERENCIA_PARIDADE_CREDITO: "Transfer com Paridade",
  VENDA: "Venda",
  HOSPEDAGEM: "Hospedagem",
  PASSAGEM: "Passagem",
  SAIDA: "Saída Manual",
  EXCLUSAO_PROGRAMA: "Exclusão de Programa",
};

const rotuloOperacao = (reg) => {
  const cat = String(reg.tra_categoria ?? reg.TRA_CATEGORIA ?? "").toUpperCase();
  const tipo = String(reg.tra_tipo ?? reg.TRA_TIPO ?? "").toUpperCase();
  let base = LABEL_CATEGORIA[cat];
  if (!base) {
    const desc = reg.tra_descricao ?? reg.TRA_DESCRICAO ?? "";
    if (desc) return String(desc);
    base = cat
      ? cat.charAt(0) + cat.slice(1).toLowerCase().replace(/_/g, " ")
      : "Operação";
  }
  if (base === "Manual") base = tipo === "SAIDA" ? "Saída Manual" : "Entrada Manual";
  return base;
};

const isSaida = (reg) =>
  String(reg.tra_tipo ?? reg.TRA_TIPO ?? "").toUpperCase() === "SAIDA" ||
  Number(reg.tra_operacao ?? reg.TRA_OPERACAO ?? 0) === 2;

// ─── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────
export default function ResumoOperacoes({ clienteId: clienteIdProp }) {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [registros, setRegistros] = useState([]);
  const [nomeMap, setNomeMap] = useState({});
  const [programaMap, setProgramaMap] = useState({});
  const [clienteId, setClienteId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Filtros
  const [fTitular, setFTitular] = useState(null);
  const [fPrograma, setFPrograma] = useState(null);
  const [fTipo, setFTipo] = useState(null);
  const [fPeriodo, setFPeriodo] = useState("mes_atual");

  // Detalhe / exclusão
  const [detalhe, setDetalhe] = useState(null);

  // Detectar tamanho da tela
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Resolve clienteId ──────────────────────────────────────────────
  useEffect(() => {
    let id = clienteIdProp;
    if (!id) {
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        id = u.cli_id || u.id;
      } catch (e) {}
      if (!id) id = parseInt(localStorage.getItem("userID") || "0") || null;
    }
    if (id) setClienteId(id);
    else message.error("Usuário não autenticado.");
  }, [clienteIdProp]);

  useEffect(() => {
    if (clienteId) carregarDados(clienteId);
  }, [clienteId]);

  const carregarDados = async (id) => {
    setLoading(true);
    try {
      const map = {};
      const progMap = {};
      
      try {
        const titRes = await api.get("/ServerPrincipal/PesquisaTitular", {
          params: { Cliente: id },
        });
        
        extractRows(titRes.data).forEach((t) => {
          const cid = t.cli_id ?? t.CLI_ID;
          const nome = t.cli_nome ?? t.CLI_NOME;
          if (cid != null && nome) {
            map[cid] = nome;
          }
          
          const carId = t.car_id ?? t.CAR_ID;
          const programa = t.car_nome_programa ?? t.CAR_NOME_PROGRAMA ?? "";
          if (carId != null && programa) {
            progMap[carId] = programa;
          }
        });
      } catch (e) {
        console.error("Erro ao carregar titulares:", e);
      }
      
      setNomeMap(map);
      setProgramaMap(progMap);

      const res = await api.get("/ServerPrincipal/PesquisaResumoOperacoes", {
        params: { Cliente: id },
      });
      setRegistros(extractRows(res.data));
    } catch (e) {
      message.error("Erro ao carregar as operações.");
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers de linha ───────────────────────────────────────────────
  const traId = (reg) => reg.tra_id ?? reg.TRA_ID;
  const cliIdDe = (reg) => reg.cli_id ?? reg.CLI_ID;
  const carIdDe = (reg) => reg.car_id ?? reg.CAR_ID;
  
  const programaDe = (reg) => {
    const carId = carIdDe(reg);
    if (carId && programaMap[carId]) {
      return programaMap[carId];
    }
    return reg.tra_programa ?? reg.TRA_PROGRAMA ?? "";
  };
  
  const nomeTitular = (reg) => nomeMap[cliIdDe(reg)] || "";
  const titularLabel = (reg) => {
    const nome = nomeTitular(reg);
    const prog = programaDe(reg);
    return [nome, prog].filter(Boolean).join(" - ") || "—";
  };
  const dataDe = (reg) => fmtTimestamp(reg.tra_data ?? reg.TRA_DATA ?? "");
  const compensacaoDe = (reg) => {
    const val = reg.tra_compensacao ?? reg.TRA_COMPENSACAO ?? reg.tra_data_compensacao ?? "";
    return fmtTimestamp(val);
  };
  const qtdeDe = (reg) => Number(reg.tra_qtde_milhas ?? reg.TRA_QTDE_MILHAS ?? 0);
  const valorDe = (reg) => Number(reg.tra_vtotal ?? reg.TRA_VTOTAL ?? 0);
  const unitDe = (reg) => Number(reg.tra_vunitario ?? reg.TRA_VUNITARIO ?? 0);
  const isPendente = (reg) => Number(reg.tra_status ?? reg.TRA_STATUS ?? 1) === 0;

  // ── Opções dos filtros ─────────────────────────────────────────────
  const titulares = useMemo(
    () => [...new Set(registros.map((r) => nomeTitular(r)).filter(Boolean))],
    [registros, nomeMap]
  );
  const programas = useMemo(
    () => [...new Set(registros.map((r) => programaDe(r)).filter(Boolean))],
    [registros, programaMap]
  );
  const tipos = useMemo(
    () => [...new Set(registros.map((r) => rotuloOperacao(r)).filter(Boolean))],
    [registros]
  );

  // ── Aplicação dos filtros ──────────────────────────────────────────
  const dentroDoPeriodo = (reg) => {
    const ym = parseYearMonth(reg.tra_data ?? reg.TRA_DATA);
    if (!ym) return true;
    const hoje = new Date();
    const anoAtual = hoje.getFullYear();
    const mesAtual = hoje.getMonth() + 1;
    if (fPeriodo === "todos") return true;
    if (fPeriodo === "mes_atual") return ym.ano === anoAtual && ym.mes === mesAtual;
    if (fPeriodo === "ano_atual") return ym.ano === anoAtual;
    if (fPeriodo === "ultimos_3") {
      const idxReg = ym.ano * 12 + ym.mes;
      const idxHoje = anoAtual * 12 + mesAtual;
      return idxHoje - idxReg >= 0 && idxHoje - idxReg <= 2;
    }
    return true;
  };

  const filtrados = useMemo(
    () =>
      registros.filter((r) => {
        if (fTitular && nomeTitular(r) !== fTitular) return false;
        if (fPrograma && programaDe(r) !== fPrograma) return false;
        if (fTipo && rotuloOperacao(r) !== fTipo) return false;
        if (!dentroDoPeriodo(r)) return false;
        return true;
      }),
    [registros, nomeMap, programaMap, fTitular, fPrograma, fTipo, fPeriodo]
  );

  // ── Exclusão CORRIGIDA ──────────────────────────────────────────────────
  const confirmarExclusao = () => {
    if (!detalhe) {
      message.warning("Nenhuma operação selecionada.");
      return;
    }
    
    const id = traId(detalhe);
    
    if (!id) {
      message.error("ID da operação não encontrado. Não é possível excluir.");
      return;
    }

    // Log para debug
    console.log("Tentando excluir operação ID:", id);
    console.log("Detalhe da operação:", detalhe);

    Modal.confirm({
      title: "Excluir esta operação?",
      icon: <ExclamationCircleOutlined style={{ color: "#e8003d" }} />,
      content: (
        <div>
          <p>A operação será removida e o saldo de milhas do cartão será revertido.</p>
          <p style={{ color: "#cf1322", fontWeight: 600, marginTop: 8 }}>
            Esta ação não pode ser desfeita.
          </p>
          <div style={{ 
            marginTop: 12, 
            padding: 12, 
            background: "#f5f6f8", 
            borderRadius: 6,
            fontSize: 12
          }}>
            <strong>Operação:</strong> {rotuloOperacao(detalhe)}<br />
            <strong>Milhas:</strong> {fmtInt(Math.abs(qtdeDe(detalhe)))}<br />
            <strong>Valor:</strong> R$ {fmtMoney(valorDe(detalhe))}
          </div>
        </div>
      ),
      okText: "Excluir operação",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          // 🔥 Enviar requisição para excluir
          const response = await api.post("/ServerPrincipal/DeletarOperacao", {
            IDTransacao: Number(id),
          });
          
          console.log("Resposta do servidor:", response.data);
          
          message.success("Operação excluída e saldo revertido com sucesso!");
          setDetalhe(null);
          
          // 🔥 Recarregar os dados
          await carregarDados(clienteId);
          
        } catch (e) {
          // 🔥 Extrair mensagem de erro de forma mais robusta
          let erro = "Erro ao excluir a operação.";
          
          if (e.response) {
            console.error("Status do erro:", e.response.status);
            console.error("Dados do erro:", e.response.data);
            
            if (typeof e.response.data === "string") {
              erro = e.response.data;
            } else if (e.response.data?.message) {
              erro = e.response.data.message;
            } else if (e.response.data?.error) {
              erro = e.response.data.error;
            }
          } else if (e.message) {
            erro = e.message;
          }
          
          message.error(erro);
          console.error("Falha ao excluir operação:", e);
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  // ── Estilos responsivos ──────────────────────────────────────────────────
  const th = {
    padding: isMobile ? "8px 10px" : "12px 16px",
    fontSize: isMobile ? 10 : 12,
    fontWeight: 600,
    color: "#8a94a6",
    textAlign: "left",
    borderBottom: "1px solid #eef0f3",
    whiteSpace: "nowrap",
  };
  
  const td = {
    padding: isMobile ? "10px 10px" : "14px 16px",
    fontSize: isMobile ? 11 : 13,
    color: "#2b3340",
    borderBottom: "1px solid #f5f6f8",
    whiteSpace: "nowrap",
  };
  
  const filtroLabel = { 
    fontSize: isMobile ? 10 : 11, 
    color: isMobile ? 'rgba(255,255,255,0.7)' : '#9fb0bd', 
    marginBottom: isMobile ? 4 : 6 
  };
  
  const detalheRow = {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    padding: isMobile ? "6px 0" : "9px 0",
    borderBottom: "1px solid #f0f0f0",
    fontSize: isMobile ? 12 : 13,
  };

  const sel = detalhe;

  // ── Colunas visíveis no mobile ──────────────────────────────────────────
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['titular', 'operacao', 'data', 'qtde', 'valor', 'actions'];
    }
    return ['titular', 'operacao', 'data', 'compensacao', 'qtde', 'valor', 'actions'];
  };

  const visibleCols = getVisibleColumns();

  const renderCell = (reg, col) => {
    const saida = isSaida(reg);
    const qtde = qtdeDe(reg);
    const programaNome = programaDe(reg);

    switch(col) {
      case 'titular':
        return (
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8 }}>
            <LogoCircle nome={programaNome} size={isMobile ? 18 : 22} />
            <span style={{ fontSize: isMobile ? 11 : 13, color: "#2b3340" }}>
              {isMobile ? titularLabel(reg).slice(0, 20) + (titularLabel(reg).length > 20 ? '...' : '') : titularLabel(reg)}
            </span>
          </div>
        );
      case 'operacao':
        return (
          <>
            {rotuloOperacao(reg)}
            {isPendente(reg) && (
              <span
                style={{
                  marginLeft: isMobile ? 4 : 8,
                  fontSize: isMobile ? 8 : 10,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: "#d48806",
                  background: "#fff7e6",
                  border: "1px solid #ffd591",
                  borderRadius: 4,
                  padding: isMobile ? "0 4px" : "1px 6px",
                  display: isMobile ? 'inline-block' : 'inline',
                }}
              >
                PENDENTE
              </span>
            )}
          </>
        );
      case 'data':
        return dataDe(reg);
      case 'compensacao':
        return compensacaoDe(reg);
      case 'qtde':
        return (
          <span style={{
            color: saida ? "#cf1322" : "#2b3340",
            fontVariantNumeric: "tabular-nums",
          }}>
            {saida ? "- " : ""}
            {fmtInt(Math.abs(qtde))}
          </span>
        );
      case 'valor':
        return fmtMoney(valorDe(reg));
      case 'actions':
        return (
          <button
            onClick={() => setDetalhe(reg)}
            title="Ver detalhe da operação"
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: isMobile ? 4 : 6,
              borderRadius: 6,
              lineHeight: 0,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "#f0f2f5")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "transparent")
            }
          >
            <MoreOutlined style={{ fontSize: isMobile ? 16 : 18, color: "#8a94a6" }} />
          </button>
        );
      default:
        return '—';
    }
  };

  const columnLabels = {
    titular: 'Titular da Conta',
    operacao: 'Operação',
    data: 'Data',
    compensacao: 'Compensação',
    qtde: 'Quantidade',
    valor: 'Valor',
    actions: ''
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f0f2f5", padding: isMobile ? "12px 8px" : "24px 16px" }}>
      <Spin spinning={loading}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* Título */}
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 8, marginBottom: isMobile ? 12 : 16 }}>
            <FileTextOutlined style={{ fontSize: isMobile ? 16 : 18, color: "#3a4756" }} />
            <span style={{ fontSize: isMobile ? 15 : 17, fontWeight: 700, color: "#1f2733" }}>
              Resumo de Operações
            </span>
          </div>

          {/* Barra de filtros - Responsiva */}
          <div
            style={{
              background: "#173240",
              borderRadius: 10,
              padding: isMobile ? "12px 12px" : "16px 20px",
              marginBottom: isMobile ? 12 : 16,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(190px, 1fr))",
              gap: isMobile ? 10 : 16,
            }}
          >
            <div>
              <div style={filtroLabel}>Filtrar por Titular</div>
              <Select
                size={isMobile ? "small" : "middle"}
                style={{ width: "100%" }}
                placeholder="Todos os titulares"
                allowClear
                value={fTitular}
                onChange={(v) => setFTitular(v ?? null)}
              >
                {titulares.map((n) => (
                  <Option key={n} value={n}>
                    {n}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <div style={filtroLabel}>Filtrar por Programa</div>
              <Select
                size={isMobile ? "small" : "middle"}
                style={{ width: "100%" }}
                placeholder="Todos programas"
                allowClear
                value={fPrograma}
                onChange={(v) => setFPrograma(v ?? null)}
              >
                {programas.map((p) => (
                  <Option key={p} value={p}>
                    {p}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <div style={filtroLabel}>Filtrar por Tipo</div>
              <Select
                size={isMobile ? "small" : "middle"}
                style={{ width: "100%" }}
                placeholder="Todas operações"
                allowClear
                value={fTipo}
                onChange={(v) => setFTipo(v ?? null)}
              >
                {tipos.map((t) => (
                  <Option key={t} value={t}>
                    {t}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <div style={filtroLabel}>Filtrar por Período</div>
              <Select
                size={isMobile ? "small" : "middle"}
                style={{ width: "100%" }}
                value={fPeriodo}
                onChange={setFPeriodo}
              >
                <Option value="mes_atual">Mês atual</Option>
                <Option value="ultimos_3">Últimos 3 meses</Option>
                <Option value="ano_atual">Ano atual</Option>
                <Option value="todos">Todos</Option>
              </Select>
            </div>
          </div>

          {/* Tabela */}
          <div
            style={{
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              overflow: "auto",
            }}
          >
            <table style={{ 
              width: "100%", 
              borderCollapse: "collapse", 
              minWidth: isMobile ? 500 : 920 
            }}>
              <thead>
                <tr>
                  {visibleCols.map((col) => (
                    <th key={col} style={{ ...th, textAlign: col === 'qtde' || col === 'valor' ? 'right' : 'left' }}>
                      {columnLabels[col]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleCols.length}
                      style={{
                        padding: isMobile ? "30px 16px" : "44px 16px",
                        textAlign: "center",
                        color: "#aab2bd",
                        fontSize: isMobile ? 12 : 13,
                      }}
                    >
                      {loading
                        ? "Carregando..."
                        : "Nenhuma operação encontrada para os filtros selecionados."}
                    </td>
                  </tr>
                ) : (
                  filtrados.map((reg, i) => {
                    return (
                      <tr
                        key={traId(reg) ?? i}
                        style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}
                      >
                        {visibleCols.map((col) => {
                          const isNumeric = col === 'qtde' || col === 'valor';
                          return (
                            <td
                              key={col}
                              style={{
                                ...td,
                                textAlign: isNumeric ? 'right' : 'left',
                                padding: isMobile ? "10px 10px" : "14px 16px",
                              }}
                            >
                              {renderCell(reg, col)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Modal de detalhe - Responsivo */}
          <Modal
            open={!!detalhe}
            onCancel={() => setDetalhe(null)}
            footer={null}
            title="Detalhe da Operação"
            width={isMobile ? "95%" : 520}
            style={{ maxWidth: '100%' }}
            bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
            destroyOnClose
          >
            {sel && (
              <Spin spinning={deleting}>
                <div style={{ marginTop: 4 }}>
                  <div
                    style={{
                      ...detalheRow,
                      display: "flex",
                      alignItems: "center",
                      gap: isMobile ? 6 : 8,
                    }}
                  >
                    <span style={{ color: "#888", fontSize: isMobile ? 12 : 13 }}>Titular / Conta</span>
                    <span
                      style={{
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: isMobile ? 6 : 8,
                        fontSize: isMobile ? 12 : 13,
                      }}
                    >
                      <LogoCircle nome={programaDe(sel)} size={isMobile ? 18 : 20} />
                      {isMobile ? titularLabel(sel).slice(0, 25) + (titularLabel(sel).length > 25 ? '...' : '') : titularLabel(sel)}
                    </span>
                  </div>
                  <div style={detalheRow}>
                    <span style={{ color: "#888" }}>Operação</span>
                    <span style={{ fontWeight: 600 }}>{rotuloOperacao(sel)}</span>
                  </div>
                  <div style={detalheRow}>
                    <span style={{ color: "#888" }}>Data</span>
                    <span>{dataDe(sel)}</span>
                  </div>
                  {compensacaoDe(sel) && (
                    <div style={detalheRow}>
                      <span style={{ color: "#888" }}>Compensação</span>
                      <span>{compensacaoDe(sel)}</span>
                    </div>
                  )}
                  <div style={detalheRow}>
                    <span style={{ color: "#888" }}>Quantidade</span>
                    <span
                      style={{
                        color: isSaida(sel) ? "#cf1322" : "#2b3340",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {isSaida(sel) ? "- " : ""}
                      {fmtInt(Math.abs(qtdeDe(sel)))} milhas
                    </span>
                  </div>
                  <div style={detalheRow}>
                    <span style={{ color: "#888" }}>Valor total</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>
                      R$ {fmtMoney(valorDe(sel))}
                    </span>
                  </div>
                  <div style={detalheRow}>
                    <span style={{ color: "#888" }}>Custo do milheiro</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>
                      R$ {fmtMoney(unitDe(sel))}
                    </span>
                  </div>
                  {(sel.tra_descricao ?? sel.TRA_DESCRICAO) && (
                    <div
                      style={{
                        ...detalheRow,
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 4,
                      }}
                    >
                      <span style={{ color: "#888" }}>Descrição</span>
                      <span style={{ color: "#444", lineHeight: 1.5, fontSize: isMobile ? 12 : 13 }}>
                        {sel.tra_descricao ?? sel.TRA_DESCRICAO}
                      </span>
                    </div>
                  )}

                  <button
                    onClick={confirmarExclusao}
                    disabled={deleting || !traId(sel)}
                    style={{
                      marginTop: isMobile ? 14 : 18,
                      width: "100%",
                      height: isMobile ? 38 : 42,
                      background: "#fff",
                      border: "1px solid #ffa39e",
                      color: "#cf1322",
                      borderRadius: 8,
                      fontSize: isMobile ? 12 : 14,
                      fontWeight: 600,
                      cursor: deleting ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: isMobile ? 6 : 8,
                      transition: "all 0.3s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fff1f0";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#fff";
                    }}
                  >
                    <DeleteOutlined /> Excluir operação
                  </button>
                  <div
                    style={{
                      marginTop: isMobile ? 6 : 8,
                      fontSize: isMobile ? 10 : 11,
                      color: "#aaa",
                      textAlign: "center",
                    }}
                  >
                    Excluir reverte o saldo de milhas do cartão. Use apenas para
                    corrigir lançamentos errados.
                  </div>
                </div>
              </Spin>
            )}
          </Modal>
        </div>
      </Spin>
    </div>
  );
}